import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";

import { v4 as uuidv4 } from "uuid";

import LSBdH2HeadLine from "../../molecules/text/linSeedKr/Bd/lsBdH2HeadLine";
import LSRgH1HeadLine from "../../molecules/text/linSeedKr/Rg/lsRgH1HeadLine";
import LSBdDisplay01 from "../../molecules/text/linSeedKr/Bd/lsBdDisplay01";
import LSBdDisplay04 from "../../molecules/text/linSeedKr/Bd/lsBdDisplay04";
import RightLabelCheckbox from "../../molecules/checkbox/rightLabelCheckbox";
import MainBtn from "../../atoms/button/mainBtn";
import LSBdDisplay02 from "../../molecules/text/linSeedKr/Bd/lsBdDisplay02";
import LSRgCaption02 from "../../molecules/text/linSeedKr/Rg/lsRgCaption02";

import useDisplay from "../../../hooks/useDisplay";
import { route } from "../../../router/route";
import { formatNumberWithCommas } from "../../../util/formatNumber";
import TermModal from "../modals/termModal";
import { errorHandler } from "../../../util/errorHandler";
import PaymentApi from "../../../api/payment.api";

const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY;
const customerKey = uuidv4();

function PaymentInfo({
  lectureInfo,
  paymentMethodValue,
  realPay,
  couponSalePrice,
  couponValue,
  isConsulting,
}) {
  const isFree =
    sessionStorage.getItem("membership") === "true" && !isConsulting;

  const { isMobile, isTablet, isDesktop } = useDisplay();
  const isSmall = isMobile || isTablet;

  const navigate = useNavigate();

  const [allAgree, setAllAgree] = useState(false);
  const [agree01, setAgree01] = useState(false);
  const [agree02, setAgree02] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (agree01 && agree02) {
      setAllAgree(true);
    } else {
      setAllAgree(false);
    }
  }, [agree01, agree02]);

  useEffect(() => {
    if (allAgree) {
      setAgree01(true);
      setAgree02(true);
    }
  }, [allAgree]);

  const [isTermModal, setIsTermModal] = useState(false);

  const onClickPayment = async () => {
    if (isProcessing || !lectureInfo?.id) return;

    try {
      if (paymentMethodValue == null || paymentMethodValue == undefined) {
        return toast("결제 수단을 선택해주세요.");
      }
      if (!isFree && realPay > 0 && !tossPayment) {
        return toast(
          "결제 모듈을 불러오는 중입니다. 잠시 후 다시 시도해주세요.",
        );
      }

      setIsProcessing(true);
      let paymentId = `${moment().format("YYMMDDHHmmss")}${Math.random()
        .toString(36)
        .slice(7)}`;

      const localData = {
        paymentFlag: isFree ? false : true,
        couponPay: couponSalePrice,
        totalPay: lectureInfo.price,
        realPay: isFree ? 0 : realPay,
        payMethod:
          paymentMethodValue === "CARD"
            ? "신용・체크 카드"
            : paymentMethodValue,
        pgPaymentId: paymentId,
      };

      if (isConsulting) {
        localData.consultingMerchId = lectureInfo.id;
      } else {
        localData.lectureVideoId = lectureInfo.id;
      }

      if (Number(couponValue) > 0) {
        localData.memberCouponIdList = [Number(couponValue)];
      }

      await PaymentApi.paymentCheck(localData);

      await onPaymentFunc(paymentId);
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const [tossPayment, setTossPayment] = useState(null);

  useEffect(() => {
    async function fetchPayment() {
      try {
        const tossPayments = await loadTossPayments(clientKey);

        // 회원 결제
        // @docs https://docs.tosspayments.com/sdk/v2/js#tosspaymentspayment
        const payment = tossPayments.payment({
          customerKey,
        });
        // 비회원 결제
        // const payment = tossPayments.payment({ customerKey: ANONYMOUS });

        setTossPayment(payment);
      } catch (error) {
        console.error("Error fetching payment:", error);
      }
    }

    fetchPayment();
  }, [clientKey, customerKey]);

  async function onPaymentFunc(paymentId) {
    try {
      const localData = {
        paymentFlag: isFree ? false : true,
        couponPay: couponSalePrice,
        totalPay: lectureInfo.price,
        realPay: isFree ? 0 : realPay,
        payMethod:
          paymentMethodValue === "CARD"
            ? "신용・체크 카드"
            : paymentMethodValue,
        pgPaymentId: paymentId,
        orderNumber: paymentId,
      };

      if (isConsulting) {
        localData.consultingMerchId = lectureInfo.id;
      } else {
        localData.lectureVideoId = lectureInfo.id;
      }

      if (Number(couponValue) > 0) {
        localData.memberCouponIdList = [Number(couponValue)];
      }

      if (!isFree && realPay > 0) {
        sessionStorage.removeItem("paymentProcessed");
        sessionStorage.setItem(
          "paymentInfo",
          JSON.stringify({
            lectureInfo: lectureInfo,
            localData: {
              ...localData,
              date: moment().format("YYYY.MM.DD HH:mm:ss"),
            },
          }),
        );

        await tossPayment.requestPayment({
          method: "CARD", // 카드 및 간편결제
          amount: {
            currency: "KRW",
            value: Number(realPay),
          },
          orderId: paymentId, // 고유 주문번호
          orderName: lectureInfo?.title,
          successUrl: window.location.origin + route.lecture_payment_complete, // 결제 요청이 성공하면 리다이렉트되는 URL
          failUrl: window.location.origin + route.lecture_payment_fail, // 결제 요청이 실패하면 리다이렉트되는 URL
          customerEmail: sessionStorage.getItem("email") || "",
          customerName: sessionStorage.getItem("name"),
          customerMobilePhone: sessionStorage.getItem("phoneNumber"),
          card: {
            useEscrow: false,
            flowMode: "DEFAULT",
            useCardPoint: false,
            useAppCardOnly: false,
          },
        });
      } else {
        await PaymentApi.payment(localData);

        navigate(route.lecture_payment_complete, {
          state: {
            lectureInfo: lectureInfo,
            localData: {
              ...localData,
              date: moment().format("YYYY.MM.DD HH:mm:ss"),
            },
          },
        });
      }
    } catch (error) {
      if (error.message === "취소되었습니다.") {
        // 결제 취소 시 사용자에게 알림
        toast("결제가 취소되었습니다.");
        return;
      }

      toast(error?.message || "결제를 처리하지 못했습니다.");
    }
  }

  return (
    <>
      <div
        style={{
          position: isSmall ? "relative" : "sticky",
          top: isSmall ? 0 : 200,
          background: "#ffffff",
          boxShadow: "0px 0px 6px 0px #0000001A",
          borderRadius: 15,
          width: isSmall ? "100%" : 482,
          padding: isMobile ? 20 : "45px 30px",
          height: "fit-content",
          marginTop: isMobile ? 20 : isTablet ? 30 : 0,
        }}
      >
        <LSBdH2HeadLine
          style={{
            color: "#5050FF",
            marginBottom: 16,
            fontSize: isMobile ? 15 : 18,
            lineHeight: isMobile ? "21px" : "25.2px",
          }}
        >
          결제정보
        </LSBdH2HeadLine>

        <div
          style={{
            width: "100%",
            height: 1,
            background: "#DBDDE0",
          }}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 10,
          }}
        >
          <LSRgH1HeadLine
            style={{
              color: "#000000",
              fontSize: isMobile ? 14 : 16,
              lineHeight: isMobile ? "19.6px" : "22.4px",
            }}
          >
            주문금액
          </LSRgH1HeadLine>
          <LSRgH1HeadLine
            style={{
              color: "#000000",
              fontSize: isMobile ? 14 : 16,
              lineHeight: isMobile ? "19.6px" : "22.4px",
            }}
          >
            {isFree ? 0 : formatNumberWithCommas(lectureInfo?.price)}원
          </LSRgH1HeadLine>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 8,
            borderBottom: isMobile ? "1px solid #DBDDE0" : "none",
            paddingBottom: isMobile ? 10 : 0,
          }}
        >
          <LSRgH1HeadLine
            style={{
              color: "#000000",
              fontSize: isMobile ? 14 : 16,
              lineHeight: isMobile ? "19.6px" : "22.4px",
            }}
          >
            쿠폰 사용
          </LSRgH1HeadLine>
          <LSRgH1HeadLine
            style={{
              color: "#000000",
              fontSize: isMobile ? 14 : 16,
              lineHeight: isMobile ? "19.6px" : "22.4px",
            }}
          >
            {formatNumberWithCommas(couponSalePrice)}원
          </LSRgH1HeadLine>
        </div>

        <LSBdDisplay01
          style={{
            color: "#6F7480",
            marginTop: 30,
            marginBottom: 16,
            fontSize: isMobile ? 15 : 20,
            lineHeight: isMobile ? "21px" : "28px",
          }}
        >
          총 결제 금액
        </LSBdDisplay01>

        <LSBdDisplay04
          style={{
            color: "#000000",
            textAlign: "right",
            marginBottom: 22,
            fontSize: isMobile ? 32 : 40,
            lineHeight: isMobile ? "44.8px" : "56px",
          }}
        >
          {isFree ? 0 : formatNumberWithCommas(realPay)}원
        </LSBdDisplay04>

        <RightLabelCheckbox
          textStyle={{
            color: "#101113",
            fontWeight: 700,
            fontSize: isMobile ? 12 : 16,
            fontFamily: "LINESeedKR-Bd",
          }}
          inputStyle={{
            marginBottom: 16,
          }}
          checked={allAgree}
          onChange={() => {
            if (allAgree) {
              setAgree01(false);
              setAgree02(false);
            }

            setAllAgree(!allAgree);
          }}
          text={"약관 전체 동의"}
        />

        <div
          style={{
            height: 1,
            background: "#DBDDE0",
            width: "100%",
            marginBottom: 16,
          }}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: isMobile ? 20 : 12,
          }}
        >
          <RightLabelCheckbox
            checked={agree01}
            onChange={() => {
              setAgree01(!agree01);
            }}
            text={"결제내용 확인 및 결제 동의(필수)"}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: isMobile ? 30 : 12,
          }}
        >
          <RightLabelCheckbox
            checked={agree02}
            onChange={() => {
              setAgree02(!agree02);
            }}
            text={"환불/취소 관련 동의(필수)"}
          />
          <LSRgCaption02
            onClick={() => {
              setIsTermModal(true);
            }}
            style={{
              textAlign: "right",
              color: "#A4A8B1",
              cursor: "pointer",
              minWidth: 42,
              textDecoration: "underline",
              fontSize: isMobile ? 10 : 12,
              lineHeight: isMobile ? "12px" : "14.4px",
            }}
          >
            전체보기
          </LSRgCaption02>
        </div>

        <MainBtn
          disabled={!allAgree || isProcessing}
          onClick={() => {
            onClickPayment();
          }}
          style={{
            marginTop: 30,
            height: isMobile ? 46 : 80,
          }}
        >
          <LSBdDisplay02
            style={{
              color: "#FFFFFF",
              textAlign: "center",
              fontSize: isMobile ? 16 : 24,
              lineHeight: isMobile ? "22.4px" : "33.6px",
            }}
          >
            {isProcessing ? "결제 처리 중..." : "결제하기"}
          </LSBdDisplay02>
        </MainBtn>
      </div>

      {isTermModal ? (
        <TermModal
          setVisiable={setIsTermModal}
          isScroll={false}
          termCategory={7}
        />
      ) : null}
    </>
  );
}

export default PaymentInfo;
